import React from 'react';
import { clsx } from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'outline';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  isLoading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
}

const LoadingSpinner: React.FC<{ size: string }> = ({ size }) => (
  <svg
    className={clsx(
      'animate-spin',
      size === 'xs' && 'w-3 h-3',
      size === 'sm' && 'w-4 h-4',
      (size === 'md' || !size) && 'w-4 h-4',
      size === 'lg' && 'w-5 h-5',
      size === 'xl' && 'w-6 h-6'
    )}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  disabled,
  className,
  type = 'button',
  ...props
}) => {
  const baseClasses = [
    'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
    'border border-transparent rounded-md',
    'select-none'
  ];

  const variantClasses = {
    primary: [
      'bg-primary-600 text-white shadow-sm',
      'hover:bg-primary-700 focus:ring-primary-500',
      'disabled:bg-primary-300'
    ],
    secondary: [
      'bg-gray-100 text-gray-900 shadow-sm border-gray-200',
      'hover:bg-gray-200 focus:ring-gray-500',
      'disabled:bg-gray-50 disabled:text-gray-400'
    ],
    outline: [
      'bg-white text-gray-700 shadow-sm border-gray-300',
      'hover:bg-gray-50 focus:ring-primary-500',
      'disabled:bg-gray-50 disabled:text-gray-400'
    ],
    ghost: [
      'bg-transparent text-gray-700 border-transparent',
      'hover:bg-gray-100 focus:ring-gray-500',
      'disabled:text-gray-400 disabled:hover:bg-transparent'
    ],
    destructive: [
      'bg-red-600 text-white shadow-sm',
      'hover:bg-red-700 focus:ring-red-500',
      'disabled:bg-red-300'
    ]
  };

  const sizeClasses = {
    xs: 'px-2 py-1 text-xs leading-4',
    sm: 'px-3 py-2 text-sm leading-4',
    md: 'px-4 py-2 text-sm leading-5',
    lg: 'px-4 py-2 text-base leading-6',
    xl: 'px-6 py-3 text-base leading-6'
  };

  const widthClasses = fullWidth ? 'w-full' : '';

  const classes = clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    widthClasses,
    className
  );

  const isDisabled = disabled || isLoading;

  return (
    <button
      type={type}
      className={classes}
      disabled={isDisabled}
      aria-busy={isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <LoadingSpinner size={size} />
          <span className="ml-2">Loading...</span>
        </>
      ) : (
        <>
          {icon && iconPosition === 'left' && (
            <span className={clsx('flex-shrink-0', children && 'mr-2')}>
              {icon}
            </span>
          )}
          {children}
          {icon && iconPosition === 'right' && (
            <span className={clsx('flex-shrink-0', children && 'ml-2')}>
              {icon}
            </span>
          )}
        </>
      )}
    </button>
  );
};